import React, { useContext, useState, useEffect } from 'react';
import { Popover, List, Tag, Spin } from 'antd';
import moment from 'moment';
import { AudienceSetSingle, AudienceSetSetVersion, AudienceSetDeleteVersion } from '../../../api/Api';
import { useSelector } from 'react-redux';
import CustomDelete from '../../../../../assets/customSVG/CustomDelete';
import NotificationContext from '../../../../../providers/NotificationProvider';
import { showErrorToast, showSuccessToast } from '../../../../../components/commonComponents/gxAntdToast/toastUtils';

export const AudienceVersionsDropdown = ({ 
  audienceSetId, 
  onVersionSelect, 
  children, 
  onVersionChange,
  currentVersion,
  onVersionPreview,
  isPreviewLoading,
  previewingVersion
}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isChangingVersion, setIsChangingVersion] = useState(false);
  const [deletingVersion, setDeletingVersion] = useState(null);
  const { currentBrand } = useSelector((store) => store.app);

  const notification = useContext(NotificationContext)

  const handleVersionSelect = async (version) => {
    if (version.is_current) {
      onVersionSelect?.(version);
      return;
    }

    try {
      setIsChangingVersion(true);
      await AudienceSetSetVersion(
        currentBrand?.brand_id, 
        audienceSetId, 
        version.version
      );

      showSuccessToast(notification,`Switched to version ${version.version}`);

      // Refresh the versions list to update the current version
      await fetchVersions();

      // Exit preview mode when setting a new version as active
      onVersionPreview?.(null);

      // Notify parent component about the version change
      onVersionChange?.(version);

      // Close the dropdown
      setIsOpen(false);
    } catch (error) {
      console.error('Error changing version:', error);
      showErrorToast(notification,'Failed to switch version');
    } finally {
      setIsChangingVersion(false);
    }
  };

  useEffect(() => {
    if (isOpen && audienceSetId) {
      fetchVersions();
    }
  }, [isOpen, audienceSetId]);

  // Reset previewing version when audienceSetId changes
  useEffect(() => {
    // Exit preview mode when audience set changes
    if (previewingVersion) {
      onVersionPreview?.(null);
    }
  }, [audienceSetId]);

  const fetchVersions = async () => {
    try {
        setLoading(true);
        const response = await AudienceSetSingle(currentBrand?.brand_id, audienceSetId);
      if (response?.data?.data?.audience_segments) {
        // Sort versions by created_at in descending order (newest first)
        const sortedVersions = [...response.data.data.audience_segments].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
          setVersions(sortedVersions);
      }
    } catch (error) {
      console.error('Error fetching audience versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionClick = (version) => {
    if (isPreviewLoading || isChangingVersion || deletingVersion) return;

    // If clicking the currently previewed version (whether it's current or not), exit preview mode
    if (previewingVersion?.version === version.version) {
      onVersionPreview?.(null);
    } else {
      // Otherwise, preview the selected version
      onVersionPreview?.(version);
    }
  };

  const handleDeleteVersion = async (version, e) => {
    e.stopPropagation();

    // Don't allow deleting the current version
    if (version.is_current) return;

    try {
      setDeletingVersion(version.version);
      await AudienceSetDeleteVersion(
        currentBrand?.brand_id,
        audienceSetId,
        version.id
      );

      showSuccessToast(notification, `Version ${version.version} deleted successfully`);

      // Refresh the versions list
      await fetchVersions();

      // If the deleted version was being previewed or was the current version
      // Load the current version data
      const currentVersion = versions.find(v => v.is_current);
      if (currentVersion) {
        onVersionSelect?.(currentVersion);
      }

      // Exit preview mode
      onVersionPreview?.(null);
    } catch (error) {
      console.error('Error deleting version:', error);
      showErrorToast(notification, 'Failed to delete version');
    } finally {
      setDeletingVersion(null);
    }
  };

  const content = (
    <div className="w-64 max-h-96 overflow-y-auto">
      <div className="p-3 font-medium text-gray-700 border-b">Version History</div>
      <List
        size="small"
        loading={loading || isChangingVersion}
        dataSource={versions}
        renderItem={(version) => (
          <List.Item 
            className={`cursor-pointer hover:bg-gray-50 p-2 ${
              version.is_current ? 'bg-blue-50' : ''
            }`}
            onClick={() => handleVersionClick(version)}
          >
            <div className="flex flex-col w-full">
              <div className="flex justify-between items-center">
                <span className="font-medium">Version {version.version}</span>
                <div className="flex items-center gap-1">
                  {version.is_current && <Tag color="blue">Current</Tag>}
                  {!version.is_current && previewingVersion?.version === version.version && (
                    <Tag color="green">Previewing</Tag>
                  )}
                  {!version.is_current && (
                    <div
                      className="text-red-500 hover:text-red-700 cursor-pointer ml-1"
                      onClick={(e) => handleDeleteVersion(version, e)}
                      disabled={deletingVersion === version.version}
                    >
                      {deletingVersion === version.version ? (
                        <Spin size="small" />
                      ) : (
                        <CustomDelete color="red" width={14} height={14} />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {moment(version.created_at).format('MMM D, YYYY h:mm A')}
              </div>
              {!version.is_current && (
                <div className="mt-1">
                  <button
                    className="text-blue-500 hover:text-blue-700 flex items-center gap-1 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVersionSelect(version);
                    }}
                    disabled={isChangingVersion}
                  >
                    <span>Set as active</span>
                  </button>
                </div>
              )}
            </div>
          </List.Item>
        )}
      />
    </div>
  );
  
  const PreviewMessage = () => {
    // Don't show preview message if not previewing or if previewing the current version
    if (!previewingVersion || previewingVersion.is_current) return null;
    
    return (
      <div className="flex items-center ml-4 text-sm" style={{ color: '#05805c' }}>
        <span>Previewing version {previewingVersion.version}</span>
      </div>
    );
  };

  return (
    <div className="flex items-center">
      <Popover
        content={content}
        trigger="click"
        placement="bottomRight"
        overlayClassName="audience-versions-popover"
        open={isOpen}
        onOpenChange={(visible) => !isPreviewLoading && !isChangingVersion && !deletingVersion && setIsOpen(visible)}
      >
        <div className="inline-block">
          {isPreviewLoading ? (
            <Spin size="small" />
          ) : (
            children
          )}
        </div>
      </Popover>
      <PreviewMessage />
    </div>
  );
};

export default AudienceVersionsDropdown;
